import { ListRequestParams } from 'modules/domain/types'
import { UsContract, UsContractStatuses } from 'modules/domain/usContracts/types'
import { FC, useCallback, useEffect, useState } from 'react'
import { UsContractDrawer } from '../Drawer/UsContractDrawer'
import { Column } from './Column'
import { useTranslation } from 'react-i18next'
import { endpoints } from 'modules/endpoints'
import { useTableData } from 'modules/domain/common/hooks'
import { CardsBoard } from 'views/components/Board/CardsBoard'
import { Progress } from '@agro-club/agroclub-shared'

type Props = {
  listRequestParams: ListRequestParams
  isFiltersLoaded: boolean
  setSelectedContractId: (id: number | undefined) => void
  selectedContractId?: number
}

const DEFAULT_MARGIN = 265

export const ClosedBoard: FC<Props> = ({
  listRequestParams,
  selectedContractId,
  setSelectedContractId,
  isFiltersLoaded,
}) => {
  const { t } = useTranslation('usContracts')
  const [selectedContract, setSelectedContract] = useState<UsContract | undefined>(undefined)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  // it is important to keep minumum deps here for performance reasons
  const onContractClick = useCallback(
    (contract: UsContract) => {
      setSelectedContractId(contract.id)
      setSelectedContract(contract)
      setIsDrawerOpen(true)
    },
    [setSelectedContractId],
  )
  const {
    data: newContracts,
    progress: newProgress,
    refetch: refetchNew,
  } = useTableData<UsContract>(endpoints.usContractsCancelledNew, listRequestParams, isFiltersLoaded)

  const {
    data: signedContracts,
    progress: signedProgress,
    refetch: refetchSigned,
  } = useTableData<UsContract>(endpoints.usContractsCancelledSigned, listRequestParams, isFiltersLoaded)

  const refetchWrapper = async (_affected?: UsContractStatuses | 'allTabs') => {
    // probably we dont need refetth here at all, cancelled contracts are not supposed to change
    // used for consistency with new tab
    await Promise.all([refetchNew(), refetchSigned()])
  }

  // update selected contract when data changes
  useEffect(() => {
    if (newProgress == Progress.WORK || signedProgress == Progress.WORK) {
      return
    }
    if (selectedContractId) {
      let selected = newContracts?.find((contract) => contract.id === selectedContractId)
      if (!selected) {
        selected = signedContracts?.find((contract) => contract.id === selectedContractId)
      }
      setSelectedContract(selected)
    }
  }, [newContracts, newProgress, selectedContractId, signedContracts, signedProgress])

  return (
    <CardsBoard>
      <Column
        onCardClick={onContractClick}
        selectedContractId={selectedContractId}
        data={newContracts}
        progress={newProgress}
        topMargin={DEFAULT_MARGIN}
        title={t('New')}
        isClosed
      ></Column>
      <Column
        onCardClick={onContractClick}
        selectedContractId={selectedContractId}
        data={signedContracts}
        progress={signedProgress}
        topMargin={DEFAULT_MARGIN}
        title={t('Signed')}
        isClosed
      ></Column>
      {isDrawerOpen && selectedContract && (
        <UsContractDrawer contract={selectedContract} refetch={refetchWrapper} onClose={() => setIsDrawerOpen(false)} />
      )}
    </CardsBoard>
  )
}
